/**
 * Station ID Utilities
 * Handles URL encoding for station IDs that may contain special characters (e.g., slashes)
 */

/**
 * Normalize a station ID value to ensure it's a string
 * Prevents "[object Object]" bugs when objects are accidentally passed as IDs
 * 
 * @param {string|object} value - The value to normalize (string, object, or null/undefined)
 * @returns {string|null} - Normalized station ID string, or null if invalid
 * 
 * @example
 * getStationId('charger001') // Returns: 'charger001'
 * getStationId({ stationId: 'charger001' }) // Returns: 'charger001'
 * getStationId({ station_id: 'charger001' }) // Returns: 'charger001'
 * getStationId({ id: 'charger001' }) // Returns: 'charger001'
 * getStationId(null) // Returns: null
 */
export function getStationId(value) {
  // Already a string - return it
  if (typeof value === 'string') {
    return value.trim() || null;
  }
  
  // Null or undefined
  if (value == null) {
    return null;
  }
  
  // Object - try common field names
  if (typeof value === 'object') {
    // Try stationId first (most common in our codebase)
    if (typeof value.stationId === 'string' && value.stationId.trim()) {
      return value.stationId.trim();
    }
    // Try station_id (server response format)
    if (typeof value.station_id === 'string' && value.station_id.trim()) {
      return value.station_id.trim();
    }
    // Try generic id field
    if (typeof value.id === 'string' && value.id.trim()) {
      return value.id.trim();
    }
    
    console.warn('[getStationId] Object provided but no valid stationId field found:', Object.keys(value));
    return null;
  }
  
  // Unexpected type
  console.warn('[getStationId] Unexpected type:', typeof value, value);
  return null;
}

/**
 * Validate that a station ID is valid (not "[object Object]" or other invalid values)
 * 
 * @param {string} stationId - The station ID to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidStationId(stationId) {
  if (!stationId || typeof stationId !== 'string') {
    return false;
  }
  
  const trimmed = stationId.trim();
  
  // Check for common bug patterns
  if (trimmed === '' || 
      trimmed === '[object Object]' || 
      trimmed.includes('[object') ||
      trimmed === 'undefined' ||
      trimmed === 'null') {
    return false;
  }
  
  return true;
}

/**
 * Encode a station ID for use in URL paths
 * Station IDs may contain slashes or other special characters that need URL encoding
 * 
 * @param {string} id - The station ID to encode
 * @returns {string} - URL-encoded station ID
 * 
 * @example
 * encodeStationId('charger001/AU-NSW-001')
 * // Returns: 'charger001%2FAU-NSW-001'
 */
export function encodeStationId(id) {
  if (!id) return '';
  return encodeURIComponent(id);
}

/**
 * Decode a station ID from URL encoding
 * 
 * @param {string} encodedId - The URL-encoded station ID
 * @returns {string} - Decoded station ID
 */
export function decodeStationId(encodedId) {
  if (!encodedId) return '';
  return decodeURIComponent(encodedId);
}
